// The HeyRoot-recorded regulated profile of a tenant (R-24 law firms, R-23 HIPAA covered entities).
//
// docs/plans/2026-10-14-j12-legal-earned-fees.md §5.1. The profile is a platform record, not a
// capability grant: one `platform.entitlements` row under the internal key `regulated_profile`,
// written only through the platform-admin regulated-profile lane (source `admin_override`, never a
// plan or trial row, never windowed, never removed), with its fields in reserved `limits` keys. The
// tenant cannot write it, and the business type a tenant picks at signup is not evidence of it.
//
// A row whose fields do not parse reads as vertical `unknown`, which the legal guards treat as legal:
// a malformed record fails closed.
import { z } from 'zod';
export const REGULATED_PROFILE_KEY = 'regulated_profile';
export const REGULATED_VERTICALS = ['legal', 'dental', 'medical', 'other'];
/**
 * The vertical stored BEFORE a regulated-profile write (#16626): `not_recorded` (no row), `unknown` (a row whose
 * fields do not parse; the guards read it as legal), or a vertical. One tuple for the engine, both request schemas
 * (root-api and the admin BFF) and the admin page, so a renamed sentinel cannot make one reject what another sends.
 */
export const REGULATED_PREVIOUS_VERTICALS = [
    'not_recorded',
    'unknown',
    ...REGULATED_VERTICALS,
];
export const REGULATED_COVERED_ENTITY_VALUES = ['yes', 'no', 'unknown'];
export const REGULATED_VERTICAL = 'regulated_vertical';
export const REGULATED_COVERED_ENTITY = 'regulated_covered_entity';
export const REGULATED_EVIDENCE_REF = 'regulated_evidence_ref';
export const REGULATED_ATTESTED_BY = 'regulated_attested_by';
export const REGULATED_ATTESTED_AT = 'regulated_attested_at';
/** Every limits key the regulated-profile lane owns. No other change may carry one. */
export const REGULATED_RESERVED_LIMIT_KEYS = [
    REGULATED_VERTICAL,
    REGULATED_COVERED_ENTITY,
    REGULATED_EVIDENCE_REF,
    REGULATED_ATTESTED_BY,
    REGULATED_ATTESTED_AT,
];
export const REGULATED_EVIDENCE_REF_MAX = 200;
/** What an operator records: the classification and the onboarding attestation reference. */
export const RegulatedProfileInputSchema = z
    .object({
    vertical: z.enum(REGULATED_VERTICALS),
    coveredEntity: z.enum(REGULATED_COVERED_ENTITY_VALUES),
    evidenceRef: z.string().trim().min(1).max(REGULATED_EVIDENCE_REF_MAX),
})
    .strict();
const ISO_MS = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
function isIsoTimestamp(v) {
    return typeof v === 'string' && ISO_MS.test(v) && new Date(v).toISOString() === v;
}
/** True when `limits` carries any key the regulated-profile lane owns. */
export function hasRegulatedLimitKey(limits) {
    return !!limits && REGULATED_RESERVED_LIMIT_KEYS.some((key) => key in limits);
}
/**
 * A complete, well-formed record: every reserved key present and valid, and nothing else. Used by the
 * write-time validator, so only a whole record authored by `actorId` can be written.
 */
export function isCompleteRegulatedProfileLimits(limits, actorId) {
    if (!limits)
        return false;
    const keys = Object.keys(limits);
    if (keys.length !== REGULATED_RESERVED_LIMIT_KEYS.length)
        return false;
    const vertical = limits[REGULATED_VERTICAL];
    const covered = limits[REGULATED_COVERED_ENTITY];
    const evidence = limits[REGULATED_EVIDENCE_REF];
    return (typeof vertical === 'string' &&
        REGULATED_VERTICALS.includes(vertical) &&
        typeof covered === 'string' &&
        REGULATED_COVERED_ENTITY_VALUES.includes(covered) &&
        typeof evidence === 'string' &&
        evidence.trim().length > 0 &&
        evidence.length <= REGULATED_EVIDENCE_REF_MAX &&
        limits[REGULATED_ATTESTED_BY] === actorId &&
        actorId.trim().length > 0 &&
        isIsoTimestamp(limits[REGULATED_ATTESTED_AT]));
}
/** Build the reserved limits for a recorded profile. The server stamps who and when. */
export function regulatedProfileLimits(input, attestedBy, attestedAt) {
    return {
        [REGULATED_VERTICAL]: input.vertical,
        [REGULATED_COVERED_ENTITY]: input.coveredEntity,
        [REGULATED_EVIDENCE_REF]: input.evidenceRef.trim(),
        [REGULATED_ATTESTED_BY]: attestedBy,
        [REGULATED_ATTESTED_AT]: attestedAt.toISOString(),
    };
}
/**
 * Read a stored row's limits into a profile. A row with unparseable fields is `unknown`
 * (fails closed for the legal guards); a missing covered-entity value is `unknown`.
 */
export function parseRegulatedProfileLimits(limits) {
    const vertical = limits?.[REGULATED_VERTICAL];
    const covered = limits?.[REGULATED_COVERED_ENTITY];
    const attested = typeof limits?.[REGULATED_ATTESTED_BY] === 'string' &&
        limits[REGULATED_ATTESTED_BY].trim().length > 0 &&
        isIsoTimestamp(limits?.[REGULATED_ATTESTED_AT]);
    return {
        recorded: true,
        vertical: attested &&
            typeof vertical === 'string' &&
            REGULATED_VERTICALS.includes(vertical)
            ? vertical
            : 'unknown',
        coveredEntity: typeof covered === 'string' &&
            REGULATED_COVERED_ENTITY_VALUES.includes(covered)
            ? covered
            : 'unknown',
    };
}
/**
 * Read a stored row's limits for display. Reuses `parseRegulatedProfileLimits` for the vertical and
 * covered entity, then adds the evidence reference and attestation (present whenever the vertical
 * reads, because the guard parser requires a valid attestation). Never throws; never returns raw limits.
 */
export function parseRegulatedProfileDisplay(limits) {
    const profile = parseRegulatedProfileLimits(limits);
    if (profile.vertical === 'unknown')
        return { unreadable: true };
    const evidence = limits?.[REGULATED_EVIDENCE_REF];
    return {
        unreadable: false,
        vertical: profile.vertical,
        coveredEntity: profile.coveredEntity,
        evidenceRef: typeof evidence === 'string' ? evidence : '',
        attestedBy: limits?.[REGULATED_ATTESTED_BY],
        attestedAt: limits?.[REGULATED_ATTESTED_AT],
    };
}
/**
 * R-24: a tenant HeyRoot recorded as legal, or whose record does not parse, collects earned fees
 * only. An unrecorded tenant is NOT legal here; failing closed for unclassified tenants is H4's gate
 * (rulings.md DL-07).
 */
export function isLegalProfile(profile) {
    return profile.recorded && (profile.vertical === 'legal' || profile.vertical === 'unknown');
}
/** The error code and operator copy for an R-24 refusal (HTTP 422 via the root error mapper). */
export const LEGAL_EARNED_FEES_ONLY_CODE = 'LEGAL_EARNED_FEES_ONLY';
export const LEGAL_EARNED_FEES_ONLY_MESSAGE = 'Law firms on HeyRoot collect earned fees against an invoice. Retainers, deposits and advance billing are not available at launch.';
//# sourceMappingURL=regulated-profile.js.map